import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { FlashDesignWithRelations } from '../../types/flashbooks';
import { getRefImagesFromFlashDesign } from '../../helpers/flashbooks';
import {
  cardActionIconButtonStyle,
  tagChipStyle,
} from '../../helpers/styles/flashbook';

interface Props {
  design: FlashDesignWithRelations;
  onClick: () => void;
  onEdit?: () => void;
  onCopyLink?: () => void;
}

const formatPriceLabel = (design: FlashDesignWithRelations) => {
  const enabledPricing = design.pricing.filter(p => p.enabled);
  if (!enabledPricing.length) return 'No estimate';
  const minPrice = Math.min(...enabledPricing.map(p => p.price));
  return `Starts at $${minPrice}`;
};

export default function FlashDesignCard({
  design,
  onClick,
  onEdit,
  onCopyLink,
}: Props) {
  const [coverUrl] = getRefImagesFromFlashDesign(design);

  const stop = (handler?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler?.();
  };

  return (
    <Stack
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: 'pointer',
        gap: 1,
        '&:hover .thumb': { transform: 'translateY(-2px)' },
        '&:hover .card-actions': { opacity: 1 },
      }}>
      <Box
        className="thumb"
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'common.black',
          border: '1px solid',
          borderColor: 'darkerLightGrey.main',
          transition: 'transform 0.15s ease-out',
        }}>
        {coverUrl ? (
          <Box
            component="img"
            src={coverUrl}
            alt={design.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : null}
        {design.isRepeatable ? (
          <Chip
            label="Repeatable"
            size="small"
            sx={{
              ...tagChipStyle,
              position: 'absolute',
              top: 10,
              left: 10,
              fontWeight: 700,
              // eslint-disable-next-line no-restricted-syntax -- typography-exception: badge glyph below the 11px scale floor
              fontSize: 10,
            }}
          />
        ) : null}
        {(onEdit || onCopyLink) && (
          <Stack
            className="card-actions"
            direction="row"
            gap={0.75}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              opacity: 0,
              transition: 'opacity 0.15s',
            }}>
            {onEdit ? (
              <IconButton
                size="small"
                onClick={stop(onEdit)}
                aria-label="Edit"
                sx={cardActionIconButtonStyle}>
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ) : null}
            {onCopyLink ? (
              <IconButton
                size="small"
                onClick={stop(onCopyLink)}
                aria-label="Copy link"
                sx={cardActionIconButtonStyle}>
                <LinkIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ) : null}
          </Stack>
        )}
      </Box>
      <Stack sx={{ px: 0.25 }}>
        <Typography variant="h4" lineHeight={1.2}>
          {design.name}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" mt={0.5}>
          {formatPriceLabel(design)}
        </Typography>
      </Stack>
    </Stack>
  );
}
